/**
 * ScheduleGanttDialog Component
 *
 * Dialog for viewing and editing existing placements in Gantt format.
 * Shows all trips in the selected time range with drag-and-drop editing.
 */

import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Shape, SpotMetadata } from "../../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import { PlacementService } from "../../../../services/PlacementService";
import { Zone } from "../../TemporalTest/types";
import { TimeRangePicker } from "./TimeRangePicker";
import { SpotOccupancyGantt } from "./SpotOccupancyGantt";
import { useScheduleGanttEdit } from "../hooks/useScheduleGanttEdit";

/**
 * Round a date to the nearest 15-minute increment
 */
function roundToQuarterHour(date: Date): Date {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  const remainder = minutes % 15;

  if (remainder === 0) {
    return rounded; // Already aligned
  }

  // Round to nearest 15-minute mark
  const roundedMinutes = remainder < 7.5
    ? minutes - remainder
    : minutes + (15 - remainder);

  rounded.setMinutes(roundedMinutes, 0, 0); // Also clear seconds and milliseconds
  return rounded;
}

interface ScheduleGanttDialogProps {
  open: boolean;
  onClose: () => void;
  initialStartTime: Date;
  initialEndTime: Date;
  currentTimestamp?: Date; // Current viewing time from main view
  rampId: string;
  rampName?: string; // Ramp display name for title
  rampShapes: Shape[];
  rampDepth: number;
  zones?: Zone[];
  placementService: PlacementService;
  onApplied?: () => void; // Callback after successful apply
  onTimeRangeChange?: (startTime: Date, endTime: Date) => void; // Callback when time range changes
}

export const ScheduleGanttDialog: React.FC<ScheduleGanttDialogProps> = ({
  open,
  onClose,
  initialStartTime,
  initialEndTime,
  currentTimestamp,
  rampId,
  rampName,
  rampShapes,
  rampDepth,
  zones,
  placementService,
  onApplied,
  onTimeRangeChange,
}) => {
  const { t } = useTranslation();
  // Time range state - round to 15-minute increments
  const [startTime, setStartTime] = useState(() => roundToQuarterHour(initialStartTime));
  const [endTime, setEndTime] = useState(() => roundToQuarterHour(initialEndTime));

  // Use schedule gantt edit hook
  const {
    isLoading,
    isApplying,
    data,
    pendingChanges,
    error,
    loadData,
    handleScheduleChanged,
    applyChanges,
    resetChanges,
  } = useScheduleGanttEdit({
    rampId,
    rampShapes,
    rampDepth,
    zones,
    placementService,
    onApplied: () => {
      if (onApplied) {
        onApplied();
      }
      onClose();
    },
  });

  // Load data only when dialog first opens (not on time range changes from drag/zoom)
  useEffect(() => {
    if (open) {
      loadData(initialStartTime, initialEndTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Only depend on 'open', not on startTime/endTime

  // Reset pending changes when dialog closes
  useEffect(() => {
    if (!open) {
      resetChanges();
    }
  }, [open, resetChanges]);

  // Handle time range change
  const handleTimeRangeChange = (newStart: Date, newEnd: Date) => {
    setStartTime(newStart);
    setEndTime(newEnd);
    // Notify parent component of time range change
    if (onTimeRangeChange) {
      onTimeRangeChange(newStart, newEnd);
    }
  };

  // Handle close
  const handleClose = () => {
    if (pendingChanges.length > 0) {
      const confirmed = window.confirm(
        t('screens.ramp.autoPark.scheduleGanttDialog.messages.unsavedChanges')
      );
      if (!confirmed) {
        return;
      }
    }
    onClose();
  };

  // Handle viewport changes from timeline (drag/zoom)
  const handleViewportChange = React.useCallback(
    (viewportStart: Date, viewportEnd: Date) => {
      // Just update the input fields, don't reload data
      setStartTime(roundToQuarterHour(viewportStart));
      setEndTime(roundToQuarterHour(viewportEnd));
    },
    []
  );

  // Calculate statistics
  const statistics = data
    ? {
        totalAircraft: data.aircraft.length,
        placedAircraft: Object.keys(data.placement).length,
        unplacedAircraft: data.aircraft.length - Object.keys(data.placement).length,
      }
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
    >
      <DialogTitle sx={{ pb: 1.5, position: "relative", pr: 7 }}>
        {/* Close Button - Absolute Top Right */}
        <IconButton
          onClick={handleClose}
          aria-label={t('screens.ramp.autoPark.scheduleGanttDialog.labels.close')}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Main Header Content */}
        <Stack spacing={1.5}>
          {/* Title with Ramp Name */}
          {rampName && (
            <Typography variant="h6" fontWeight={600}>
              {t('screens.ramp.autoPark.scheduleGanttDialog.title', { rampName })}
            </Typography>
          )}

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TimeRangePicker
              startTime={startTime}
              endTime={endTime}
              onChange={handleTimeRangeChange}
            />
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            {/* Statistics */}
            {statistics && (
              <>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('screens.ramp.autoPark.scheduleGanttDialog.statistics.aircraftPlaced')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {statistics.placedAircraft} / {statistics.totalAircraft}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('screens.ramp.autoPark.scheduleGanttDialog.statistics.avgUtilization')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {Math.round(
                      (statistics.placedAircraft / (statistics.totalAircraft || 1)) * 100
                    )}%
                  </Typography>
                </Box>
              </>
            )}

            <Box sx={{ ml: "auto" }}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                size="small"
              >
                {t('screens.ramp.autoPark.scheduleGanttDialog.buttons.print')}
              </Button>
            </Box>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        <Stack spacing={2} height="100%">
          {/* Loading State */}
          {isLoading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "#ffebee",
                border: "1px solid #ef5350",
                borderRadius: 2,
              }}
            >
              <Typography color="error">{error}</Typography>
            </Paper>
          )}

          {/* Gantt Chart */}
          {!isLoading && !error && data && (
            <Box flex={1} minHeight={0}>
              <SpotOccupancyGantt
                spots={rampShapes
                  .filter((s) => s.type === "Spot")
                  .map((s) => {
                    const spotMetadata = s.metadata as SpotMetadata | undefined;
                    return {
                      id: s.id,
                      zoneId: spotMetadata?.zoneId || "default",
                      allowedSizes: spotMetadata?.allowedSizes || [],
                      occupancy: new BigUint64Array(), // Empty occupancy (will be calculated from placements)
                      orientations: [{ id: "center" }],
                      shape: s,
                    };
                  })}
                aircraft={data.aircraft}
                placement={data.placement}
                horizonHours={data.horizonHours}
                startTime={data.startTime}
                currentTimestamp={currentTimestamp}
                rampShapes={rampShapes}
                zones={data.zones}
                statistics={statistics}
                hideStatistics={true}
                onScheduleChanged={handleScheduleChanged}
                onViewportChange={handleViewportChange}
                enableEditing={true}
              />
            </Box>
          )}

          {/* Pending Changes Indicator */}
          {pendingChanges.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: "#fff3e0",
                border: "1px solid #ffb74d",
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" color="warning.dark">
                {t('screens.ramp.autoPark.scheduleGanttDialog.messages.pendingChanges', {
                  count: pendingChanges.length
                })}
              </Typography>
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isApplying}>
          {t('screens.ramp.autoPark.scheduleGanttDialog.buttons.close')}
        </Button>
        <Button
          variant="contained"
          startIcon={isApplying ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={applyChanges}
          disabled={isApplying || pendingChanges.length === 0}
        >
          {isApplying
            ? t('screens.ramp.autoPark.scheduleGanttDialog.buttons.applying')
            : t('screens.ramp.autoPark.scheduleGanttDialog.buttons.applyToDatabase')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
